// src/pages/TenantManagement/index.tsx
import React, { useEffect } from 'react';
import { getTenantManagers } from 'helpers/fakebackend_helper';
import { Container, Row, Col, Card, CardBody, CardHeader, Button } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

// Components
import BreadCrumb from 'Components/Common/BreadCrumb';
import TenantList from './TenantList';
import TenantFilters from './TenantFilters';
import TenantPagination from './TenantPagination';
import CreateTenantModal from './modals/CreateTenantModal';
import EditTenantModal from './modals/EditTenantModal';
import DeleteTenantDialog from './modals/DeleteTenantDialog';

// Redux
import {
  setSelectedTenant,
  openModal,
  closeModal,
} from 'slices/tenantManagement/reducer';
import {
  fetchTenants,
  deleteTenantById,
} from 'slices/tenantManagement/thunk';

// Types
import { Tenant } from './types';

// Hooks
import { useBrandingTitle } from '../../hooks';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('tenant_management');

const TenantManagement: React.FC = () => {
  const dispatch = useDispatch<any>();
  useBrandingTitle(st('dm_phong_chi_nhanh_text'));
  const [managers, setManagers] = React.useState<any[]>([]);
  const [loadingManagers, setLoadingManagers] = React.useState(false);
  
  // Redux state
  const {
    tenants,
    selectedTenant,
    loading,
    pagination,
    filters,
    sortBy,
    sortOrder,
    modalState,
    error,
  } = useSelector((state: any) => state.TenantManagement);
  
  // Load tenants when filters/pagination/sorting change
  useEffect(() => {
    loadTenants();
  }, [filters, sortBy, sortOrder, pagination.currentPage, pagination.pageSize]);
  
  // Show error toast
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);
  
  const loadTenants = () => {
    const params: any = {
      PageIndex: pagination.currentPage,
      PageSize: pagination.pageSize,
      search: filters.searchTerm || undefined,
      status: filters.status !== 'All' ? filters.status : undefined,
      parentId: filters.parentId || undefined,
      sortBy,
      sortOrder,
    };
    
    // Remove undefined values
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined)
    );
    
    dispatch(fetchTenants(cleanParams));
  };
  
    // Fetch managers list
    const fetchManagers = async () => {
      try {
        setLoadingManagers(true);
        const response = await getTenantManagers();
        // Unwrap response
        const data = response.data || response;
        // Handle both array and paginated response
        const managerList = Array.isArray(data) 
          ? data 
          : (data.items || data.data || []);
        setManagers(managerList);
      } catch (error: any) {
        console.error('Error fetching managers:', error);
        toast.error(st('khong_the_tai_danh_sach_quan_message'));
        setManagers([]);
      } finally {
        setLoadingManagers(false);
      }
    };
  
  // Handlers
  const handleCreateTenant = () => {
    fetchManagers();
    dispatch(openModal('create'));
  };
  
    const handleEditTenant = (tenant: Tenant) => {
    fetchManagers();
    dispatch(setSelectedTenant(tenant));
    dispatch(openModal('edit'));
  };
  
  const handleDeleteTenant = (tenant: Tenant) => {
    dispatch(setSelectedTenant(tenant));
    dispatch(openModal('delete'));
  };
  
    const handleConfirmDelete = async () => {
    if (selectedTenant) {
      try {
        await dispatch(deleteTenantById(selectedTenant.id)).unwrap();
        toast.success(st('xoa_don_vi_thanh_cong_message'));
        dispatch(closeModal('delete'));
        dispatch(setSelectedTenant(null));
        loadTenants(); // Reload list
      } catch (error: any) {
        toast.error(error || st('co_loi_xay_ra_khi_xoa_message'));
      }
    }
  };
  
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
                    {/* Breadcrumb */}
          <BreadCrumb title={st('dm_phong_chi_nhanh_text')} pageTitle={st('quan_ly_text')} />
          
          <Row>
            <Col lg={12}>
                            <Card>
                <CardHeader>
                  {/* Filters với nút Thêm mới */}
                  <TenantFilters onCreateTenant={handleCreateTenant} />
                </CardHeader>
                
                <CardBody>
                  
                  <TenantList
                    tenants={tenants}
                    loading={loading}
                    onEdit={handleEditTenant}
                    onDelete={handleDeleteTenant}
                    page={pagination.currentPage}
                    pageSize={pagination.pageSize}
                  />
                  
                  <TenantPagination />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
            </div>
      
            {/* Modals */}
            <CreateTenantModal
              isOpen={modalState.create}
              toggle={(reload) => {
                dispatch(closeModal('create'));
                if (reload) loadTenants();
              }}
              tenants={tenants}
              managers={managers}
            />
      
            <EditTenantModal
              isOpen={modalState.edit}
              toggle={(reload) => {
                dispatch(closeModal('edit'));
                dispatch(setSelectedTenant(null));
                if (reload) loadTenants();
              }}
              tenant={selectedTenant}
              tenants={tenants}
              managers={managers}
            />
      
      <DeleteTenantDialog
        isOpen={modalState.delete}
        onClose={() => dispatch(closeModal('delete'))}
        onDelete={handleConfirmDelete}
        tenant={selectedTenant}
      />
    </React.Fragment>
  );
};

export default TenantManagement;